import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from '../../global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  createFirmwareGroup as createFirmwareGroupAPI,
  deleteFirmwareGroup as deleteFirmwareGroupAPI,
  getFirmwareGroups,
  updateFirmwareGroup as updateFirmwareGroupAPI,
} from './firmwareGroups.api';
import { FirmwareGroup } from './firmwareGroups.types';

const name = 'firmwares';

const firmwareGroupEntityAdapter = createEntityAdapter<AsyncEntity<FirmwareGroup>>({
  selectId: firmwareGroup => firmwareGroup.id,
});

export const firmwareGroupSelectors = firmwareGroupEntityAdapter.getSelectors();

export const fetchFirmwareGroups = createAsyncThunkFromFetch(`${name}/fetchFirmwareGroups`, getFirmwareGroups);
export const deleteFirmwareGroup = createAsyncThunkFromFetch(`${name}/deleteFirmwareGroup`, deleteFirmwareGroupAPI);
export const createFirmwareGroup = createAsyncThunkFromFetch(`${name}/createFirmwareGroup`, createFirmwareGroupAPI);
export const updateFirmwareGroup = createAsyncThunkFromFetch(`${name}/updateFirmwareGroup`, updateFirmwareGroupAPI);

const firmwareGroupsSlice = createSlice({
  name,
  initialState: firmwareGroupEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchFirmwareGroups.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchFirmwareGroups.fulfilled, (state, { payload }) => {
      firmwareGroupEntityAdapter.upsertMany(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchFirmwareGroups.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteFirmwareGroup.pending, (state, { meta }) => {
      firmwareGroupEntityAdapter.updateOne(state, {
        id: meta.arg.id,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteFirmwareGroup.fulfilled, (state, { meta }) => {
      firmwareGroupEntityAdapter.removeOne(state, meta.arg.id);
    });
    builder.addCase(deleteFirmwareGroup.rejected, (state, { meta }) => {
      firmwareGroupEntityAdapter.updateOne(state, {
        id: meta.arg.id,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createFirmwareGroup.pending, (state, { meta }) => {
      firmwareGroupEntityAdapter.addOne(state, {
        ...meta.arg,
        isPending: true,
      });
    });
    builder.addCase(createFirmwareGroup.fulfilled, (state, { meta }) => {
      firmwareGroupEntityAdapter.updateOne(state, {
        id: meta.arg.id,
        changes: { isPending: false },
      });
    });
    builder.addCase(createFirmwareGroup.rejected, (state, { meta }) => {
      firmwareGroupEntityAdapter.updateOne(state, {
        id: meta.arg.id,
        changes: { isPending: false, hasError: true },
      });
    });
  },
});

const { reducer } = firmwareGroupsSlice;

export default reducer;
